import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Clock, MapPin,
  FolderKanban, AlertTriangle, Calculator, Pencil, Trash2, X
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const TYPE_CONFIG = {
  manuale:         { label: 'Manuale',        dot: 'bg-blue-500',   badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  progetto_inizio: { label: 'Inizio Progetto', dot: 'bg-green-500', badge: 'bg-green-50 text-green-700 border-green-200' },
  progetto_fine:   { label: 'Fine Progetto',  dot: 'bg-red-500',    badge: 'bg-red-50 text-red-700 border-red-200' },
  scadenza:        { label: 'Scadenza',       dot: 'bg-amber-500',  badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  sal:             { label: 'SAL',            dot: 'bg-purple-500',  badge: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTH_NAMES = [
  'Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
  'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'
];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  const d = new Date(year, month - 1, 1).getDay();
  return d === 0 ? 6 : d - 1; // Monday=0
}

export default function CalendarPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'aspp', 'capo_cantiere', 'qhse');

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayPanel, setShowDayPanel] = useState(false);

  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '', date: '', time: '', end_time: '', location: '', description: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (typeFilter !== 'all') params.event_type = typeFilter;
      const res = await api.get('/calendar/all-events', { params });
      setEvents(res.data.events || []);
      setKpi(res.data.kpi || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, month, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
  };

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    }
    return map;
  }, [events]);

  // Calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const calendarCells = useMemo(() => {
    const cells = [];
    // Leading empty cells
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, dateStr, events: eventsByDate[dateStr] || [] });
    }
    return cells;
  }, [firstDay, daysInMonth, year, month, eventsByDate]);

  const handleDayClick = (cell) => {
    if (!cell) return;
    setSelectedDate(cell.dateStr);
    setShowDayPanel(true);
  };

  const selectedDayEvents = selectedDate ? (eventsByDate[selectedDate] || []) : [];

  // Event CRUD
  const openCreateEvent = (date) => {
    setEditingEvent(null);
    setEventForm({ title: '', date: date || '', time: '', end_time: '', location: '', description: '' });
    setShowEventDialog(true);
  };

  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    setEventForm({
      title: ev.title, date: ev.date, time: ev.time || '',
      end_time: ev.end_time || '', location: ev.location || '', description: ev.description || ''
    });
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    try {
      if (editingEvent) {
        await api.put(`/calendar/events/${editingEvent.source_id}`, eventForm);
      } else {
        await api.post('/calendar/events', eventForm);
      }
      setShowEventDialog(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteEvent = async (ev) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try {
      await api.delete(`/calendar/events/${ev.source_id}`);
      load();
      if (selectedDayEvents.length <= 1) setShowDayPanel(false);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${parseInt(d)} ${MONTH_NAMES[parseInt(m) - 1]} ${y}`;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Calendario</h1>
          <p className="text-sm text-slate-500 mt-1">Eventi, progetti, scadenze e SAL in un'unica vista</p>
        </div>
        {canManage && (
          <Button onClick={() => openCreateEvent('')} data-testid="add-event-btn">
            <Plus size={16} className="mr-1" /> Nuovo Evento
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard icon={CalendarDays} label="Totale Eventi" value={kpi.totale || 0} color="text-slate-700" bg="bg-slate-50" testId="kpi-totale" />
        <KpiCard icon={CalendarDays} label="Manuali" value={kpi.manuali || 0} color="text-blue-700" bg="bg-blue-50" testId="kpi-manuali" />
        <KpiCard icon={FolderKanban} label="Progetti" value={kpi.progetti || 0} color="text-green-700" bg="bg-green-50" testId="kpi-progetti" />
        <KpiCard icon={AlertTriangle} label="Scadenze" value={kpi.scadenze || 0} color="text-amber-700" bg="bg-amber-50" testId="kpi-scadenze" />
        <KpiCard icon={Calculator} label="SAL" value={kpi.sal || 0} color="text-purple-700" bg="bg-purple-50" testId="kpi-sal" />
      </div>

      {/* Navigation + Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth} data-testid="cal-prev-month">
            <ChevronLeft size={18} />
          </Button>
          <h2 className="text-lg font-semibold text-slate-900 min-w-[200px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </h2>
          <Button variant="outline" size="icon" onClick={nextMonth} data-testid="cal-next-month">
            <ChevronRight size={18} />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToday} className="ml-2 text-xs" data-testid="cal-today-btn">
            Oggi
          </Button>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52" data-testid="cal-type-filter">
            <SelectValue placeholder="Tutti i tipi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="manuale">Manuali</SelectItem>
            <SelectItem value="progetto_inizio">Inizio Progetto</SelectItem>
            <SelectItem value="progetto_fine">Fine Progetto</SelectItem>
            <SelectItem value="scadenza">Scadenze</SelectItem>
            <SelectItem value="sal">SAL</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Calendar Grid */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400">Caricamento...</div>
          ) : (
            <>
              {/* Weekday header */}
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {WEEKDAYS.map(d => (
                  <div key={d} className="py-2.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {d}
                  </div>
                ))}
              </div>
              {/* Days grid */}
              <div className="grid grid-cols-7">
                {calendarCells.map((cell, idx) => {
                  const isToday = cell?.dateStr === todayStr;
                  const hasEvents = cell?.events.length > 0;
                  return (
                    <div
                      key={idx}
                      onClick={() => handleDayClick(cell)}
                      className={`
                        min-h-[90px] md:min-h-[110px] border-b border-r border-slate-100 p-1.5 transition-colors
                        ${cell ? 'cursor-pointer hover:bg-slate-50' : ''}
                        ${isToday ? 'bg-blue-50/50' : ''}
                        ${selectedDate === cell?.dateStr ? 'ring-2 ring-blue-400 ring-inset' : ''}
                      `}
                      data-testid={cell ? `cal-day-${cell.dateStr}` : `cal-empty-${idx}`}
                    >
                      {cell && (
                        <>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`
                              text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
                              ${isToday ? 'bg-blue-600 text-white' : 'text-slate-700'}
                            `}>
                              {cell.day}
                            </span>
                            {hasEvents && (
                              <span className="text-[10px] text-slate-400 font-medium">{cell.events.length}</span>
                            )}
                          </div>
                          <div className="space-y-0.5">
                            {cell.events.slice(0, 3).map(ev => (
                              <div
                                key={ev.id}
                                className={`flex items-center gap-1 px-1 py-0.5 rounded text-[11px] leading-tight truncate ${TYPE_CONFIG[ev.type]?.badge || 'bg-slate-100 text-slate-600'}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TYPE_CONFIG[ev.type]?.dot || 'bg-slate-400'}`} />
                                <span className="truncate">{ev.title}</span>
                              </div>
                            ))}
                            {cell.events.length > 3 && (
                              <span className="text-[10px] text-slate-400 pl-1">+{cell.events.length - 3} altri</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 items-center">
        {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </div>
        ))}
      </div>

      {/* Day Detail Panel */}
      <Dialog open={showDayPanel} onOpenChange={setShowDayPanel}>
        <DialogContent className="max-w-lg max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays size={18} />
              {formatDateDisplay(selectedDate)}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[55vh]">
            {selectedDayEvents.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">Nessun evento per questa data</p>
            ) : (
              <div className="space-y-3">
                {selectedDayEvents.map(ev => (
                  <div key={ev.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors" data-testid={`day-event-${ev.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={`text-[10px] ${TYPE_CONFIG[ev.type]?.badge}`}>
                            {TYPE_CONFIG[ev.type]?.label}
                          </Badge>
                          {ev.time && (
                            <span className="flex items-center gap-0.5 text-[11px] text-slate-500">
                              <Clock size={10} />
                              {ev.time}{ev.end_time ? ` - ${ev.end_time}` : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-slate-900 truncate">{ev.title}</p>
                        {ev.description && <p className="text-xs text-slate-500 mt-0.5">{ev.description}</p>}
                        {ev.location && (
                          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                            <MapPin size={10} /> {ev.location}
                          </p>
                        )}
                      </div>
                      {ev.editable && canManage && (
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => openEditEvent(ev)} data-testid={`edit-event-${ev.id}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDeleteEvent(ev)} data-testid={`delete-event-${ev.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            {canManage && (
              <Button size="sm" onClick={() => { setShowDayPanel(false); openCreateEvent(selectedDate); }} data-testid="add-event-from-day">
                <Plus size={14} className="mr-1" /> Aggiungi Evento
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowDayPanel(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Modifica Evento' : 'Nuovo Evento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input
                value={eventForm.title}
                onChange={e => setEventForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Titolo dell'evento"
                data-testid="event-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Data *</Label>
              <Input
                type="date"
                value={eventForm.date}
                onChange={e => setEventForm(p => ({ ...p, date: e.target.value }))}
                data-testid="event-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Ora Inizio</Label>
                <Input
                  type="time"
                  value={eventForm.time}
                  onChange={e => setEventForm(p => ({ ...p, time: e.target.value }))}
                  data-testid="event-time-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Ora Fine</Label>
                <Input
                  type="time"
                  value={eventForm.end_time}
                  onChange={e => setEventForm(p => ({ ...p, end_time: e.target.value }))}
                  data-testid="event-endtime-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Luogo</Label>
              <Input
                value={eventForm.location}
                onChange={e => setEventForm(p => ({ ...p, location: e.target.value }))}
                placeholder="Luogo o link riunione"
                data-testid="event-location-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Textarea
                value={eventForm.description}
                onChange={e => setEventForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Dettagli dell'evento"
                rows={3}
                data-testid="event-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>Annulla</Button>
            <Button
              onClick={handleSaveEvent}
              disabled={!eventForm.title || !eventForm.date}
              data-testid="event-save-btn"
            >
              {editingEvent ? 'Salva Modifiche' : 'Crea Evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, bg, testId }) {
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
          <Icon size={18} className={color} strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
